import type { ItemStatus } from '@/data/tracks'

// How far along an exam's material is — the one definition of "is this exam
// ready to study from?", keyed by the exam_progress key used everywhere else
// (`P`, `FM`, `MAS-I`, `CAS-5`, `CAS-7`, … — see `wikiExamIdToProgressKey`).
//
// Three states:
//   'ready'       — a mature syllabus page plus a full question bank, all of it
//                   fact checked and every question answerable from its pages:
//                   every gate of `scripts/publication_readiness.py` passes.
//                   Exams P and FM (ready again 2026-10-30, all eight gates)
//                   and MAS-I, the three DISCs and Exam 7 (2026-10-31). All
//                   seven were ready before, and went back to beta from
//                   2026-10-25 until their banks passed answerability (G8).
//                   MAS-II, the first beta exam to pass all eight (2026-10-31),
//                   and Exams 9 and 5 (2026-10-31)
//   'beta'        — usable, still being filled out (Exams
//                   6C and 8 — their banks converted from the released
//                   papers — FAM and SRM, from the SOA's sample sets, ALTAM and
//                   ASTAM from their sample sets and released papers, and
//                   PCPA, which has no paper to convert)
//   'development' — syllabus scaffolding only: no question bank, concept
//                   pages mostly unwritten. Not something a candidate can
//                   study from yet (Exams 6U, 6I and 6T, the SOA exams past
//                   FM, the CIA's ACIA and FCIA exams and modules, and the
//                   non-exam requirements — VEE, the modules and the
//                   professionalism courses).
//
// An exam moves up when `scripts/publication_readiness.py --check` says so:
// development → beta once every objective links a written, specific page and
// every concept has a question; beta → ready once all of it is fact checked
// and every question is answerable from its pages (docs/content-plan.md).
//
// Surfaces read this rather than re-deriving "not P and not FM" locally: the
// exam rows on the Study Guides and Quiz tabs (`components/ExamRow.tsx`, its
// status tag) and the exam page's status banner (`pages/wiki/WikiExam.tsx` →
// `WikiFloatingSearch`).
export type ExamStatus = 'ready' | 'beta' | 'development'

/** Exams whose material is finished enough to carry no status label at all: each passes all eight
 * gates of `publication_readiness.py --check` (P and FM again since 2026-10-30, MAS-I, the DISCs and Exam 7 since 2026-10-31, after G8 emptied it on 2026-10-25; MAS-II, Exam 9 and Exam 5 since 2026-10-31; SRM and FAM since 2026-10-31). */
const READY_EXAMS = new Set<string>(['P', 'FM', 'MAS-I', 'CAS-DA', 'CAS-RM', 'CAS-IA', 'CAS-7', 'MAS-II', 'CAS-9', 'CAS-5', 'SRM', 'FAM'])

/**
 * Exams that exist only as a syllabus outline so far. Greyed out wherever they
 * are listed — they are visible so candidates can see what is coming, not
 * because they are usable.
 */
// (PCPA has no bank — CAS releases no PCPA paper or sample questions to
// convert — but it is beta: its page and the Projects tab's simulator are
// material to study from. The three DISCs left this set once their pages were
// written section by section and their banks — the vault's own questions,
// since The Institutes publishes none — covered every concept.)
// SRM left this set once the SOA's sample set was converted into its bank and
// five of the vault's own questions covered the concepts the set doesn't;
// FAM the same way, from its two sets (FAM-S and FAM-L) and six of the
// vault's own; ASTAM from its written-answer sample set and its seven released
// sittings (Spring 2023 to Spring 2026), and two of the vault's own; ALTAM from
// its sample set and seven sittings alone.
// The SOA exams past FM — PA, the ATPA assessment and
// the FSA pathway's fellowship exams — have pages transcribed from their
// syllabi by the syllabus pipeline (docs/syllabus-pipeline.md), and nothing else
// yet: no question bank, concept pages mostly unwritten.
// The non-exam requirements — the three VEEs, the SOA's e-learning modules
// (PAF, ASF, FAP, DMAC), and the professionalism courses (the SOA's APC and FAC,
// the CAS's Course on Professionalism, keyed `CAS-APC`) — have pages transcribed
// from what the bodies publish about them, and no questions: there is nothing
// here to study them from yet.
const IN_DEVELOPMENT_EXAMS = new Set([
  'VEE-MS', 'VEE-ECON', 'VEE-AF',
  'PAF', 'ASF', 'FAP', 'APC', 'FSA-DMAC', 'FSA-FAC', 'CAS-APC',
  'PA', 'ATPA',
  'FSA-CFE101', 'FSA-CFE201',
  'FSA-CP311', 'FSA-CP312', 'FSA-CP321', 'FSA-CP341', 'FSA-CP351',
  'FSA-GH101', 'FSA-GH201', 'FSA-GH301',
  'FSA-GI101', 'FSA-GI201', 'FSA-GI301', 'FSA-GI302',
  'FSA-ILA101', 'FSA-ILA201',
  'FSA-INV101', 'FSA-INV201',
  'FSA-RET101', 'FSA-RET201', 'FSA-RET301',
  // The CIA's own pathway: the ACIA modules and Capstone Exam, and each FCIA track's two modules
  // and three exams (F3BNK the banking option for F3FIE) — pages transcribed
  // from the CIA's syllabi by the same pipeline, and nothing else yet.
  'CIA-ACIA-M1', 'CIA-ACIA-M2', 'CIA-ACIA-CAPSTONE',
  'CIA-PC-M1', 'CIA-PC-M2', 'CIA-F1PC', 'CIA-F2PC', 'CIA-F3PC',
  'CIA-ILA-M1', 'CIA-ILA-M2', 'CIA-F1ILA', 'CIA-F2ILA', 'CIA-F3ILA',
  'CIA-GB-M1', 'CIA-GB-M2', 'CIA-F1GB', 'CIA-F2GB', 'CIA-F3GB',
  'CIA-RET-M1', 'CIA-RET-M2', 'CIA-F1RET', 'CIA-F2RET', 'CIA-F3RET',
  'CIA-FIE-M1', 'CIA-FIE-M2', 'CIA-F1FIE', 'CIA-F2FIE', 'CIA-F3FIE', 'CIA-F3BNK',
])

/**
 * Variants of a shared progress key whose material lags the rest of the key.
 * Exam 6 is sat in regional variants that share `CAS-6`: 6C has its bank (the
 * Fall 2013–Fall 2019 Exam 6-Canada papers) and is beta; 6U, 6I
 * (International) and 6T (Taipei, credited from the AICT's own exams) have none
 * and are still a syllabus outline. Keyed by the page's own exam id — the ids
 * `bankLabelFor` in `lib/examIds.ts` and `LOCALIZED_EXAM_VARIANT_IDS` use.
 */
const IN_DEVELOPMENT_VARIANTS: Record<string, ReadonlySet<string>> = {
  'CAS-6': new Set(['6U', '6I', '6T']),
}

/**
 * `examId` is the exam page's own id (`6C`, `6U`) where a surface knows which
 * page it is drawing. Without one a shared key reads as its furthest-along
 * variant — `CAS-6` is beta, since 6C can be studied.
 */
export function examStatus(progressKey: string | null | undefined, examId?: string | null): ExamStatus {
  if (!progressKey) return 'beta'
  if (READY_EXAMS.has(progressKey)) return 'ready'
  if (IN_DEVELOPMENT_EXAMS.has(progressKey)) return 'development'
  if (examId && IN_DEVELOPMENT_VARIANTS[progressKey]?.has(examId)) return 'development'
  return 'beta'
}

/** True for the exams that are still scaffolding (Exams 6U/6I/6T, the SOA exams past FM, the non-exam requirements). */
export function isExamInDevelopment(progressKey: string | null | undefined, examId?: string | null): boolean {
  return examStatus(progressKey, examId) === 'development'
}

/** True for the exams that are usable but still being filled out. */
export function isExamBeta(progressKey: string | null | undefined, examId?: string | null): boolean {
  return examStatus(progressKey, examId) === 'beta'
}

/** Label shown on the status pill / banner, or null when there is nothing to say. */
export const EXAM_STATUS_LABEL: Record<ExamStatus, string | null> = {
  ready: null,
  beta: 'Beta',
  development: 'In Development',
}

/**
 * What the end of a credential-path row offers (`components/ExamsPopout.tsx`).
 *
 *   'add'  — nothing started and there is material to study: the Add button,
 *            which marks the exam in progress and opens the study-plan wizard
 *   'plan' — being studied: set or change the exam date
 *   'none' — nothing to offer, for one of two reasons the row states itself.
 *            Either the material isn't there ("In development" / "Not covered
 *            yet"), or the exam is passed — and a passed exam is struck
 *            through, so offering to add it read as "start studying the exam
 *            you just ticked off". Un-tick the dot and 'add' comes back.
 *
 * `canStudy` is `examStatus` above reduced to a yes/no: 'ready' or 'beta'.
 */
export type ExamRowAction = 'add' | 'plan' | 'none'

export function examRowAction(status: ItemStatus, canStudy: boolean): ExamRowAction {
  if (!canStudy) return 'none'
  if (status === 'in_progress') return 'plan'
  return status === 'not_started' ? 'add' : 'none'
}
